/**
 * Users API helpers.
 */

/**
 * Default avatar palette: animal emoji + background color pulled from the
 * Tailwind token set. Paired deterministically by user_id so each user keeps
 * the same "animal + color" avatar across renders until they upload a pic.
 */
export const DEFAULT_AVATAR_EMOJIS = [
  '🦊', '🦁', '🐯', '🐨', '🐼', '🐸', '🦉', '🦄',
  '🐙', '🦋', '🐢', '🐝', '🐳', '🦜', '🐧', '🦩',
];

export const DEFAULT_AVATAR_COLORS = [
  '#6B8CFF', // ether
  '#8B7CFF', // aura
  '#5FA8A1', // mist
  '#FF6B6B', // energy
  '#4ECDC4', // wisdom
  '#FF8B94', // love
];

function hashUserId(userId) {
  const n = Number(userId);
  if (!Number.isFinite(n) || n === 0) return 0;
  // Fractional part of sin is well-spread across integer inputs.
  return Math.floor(Math.abs(Math.sin(n + 1) * 10000)) || 0;
}

export function getDefaultAvatarEmoji(userId) {
  const hash = hashUserId(userId);
  return DEFAULT_AVATAR_EMOJIS[hash % DEFAULT_AVATAR_EMOJIS.length];
}

export function getDefaultAvatarColor(userId) {
  const hash = hashUserId(userId);
  return DEFAULT_AVATAR_COLORS[
    Math.floor(hash / DEFAULT_AVATAR_EMOJIS.length) % DEFAULT_AVATAR_COLORS.length
  ];
}

export async function followUser(targetUserId) {
  const res = await fetch('/api/users/follow', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_user_id: targetUserId }),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to follow');
  }
  const data = await res.json();
  return { status: data?.status ?? 'accepted' };
}

export async function unfollowUser(targetUserId) {
  const res = await fetch('/api/users/follow', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_user_id: targetUserId }),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to unfollow');
  return res.json();
}

export async function getFollowStatus(targetUserId) {
  const res = await fetch(`/api/users/follow-status/${targetUserId}`, { credentials: 'include' });
  if (!res.ok) return null;
  const data = await res.json();
  return data.status;
}

export async function getFollowers(userId) {
  const res = await fetch(`/api/users/${userId}/followers`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch followers');
  const data = await res.json();
  return data.users || [];
}

export async function getFollowing(userId) {
  const res = await fetch(`/api/users/${userId}/following`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch following');
  const data = await res.json();
  return data.users || [];
}

/**
 * Normalize GET /api/users/user JSON (envelope from server).
 * @returns {{ access: string, feed_access: string, profile: object|null, poster: object|null, isOwnProfile: boolean, follow_status: string|null }}
 */
export function normalizeUserProfileResponse(body) {
  if (!body || typeof body !== 'object') {
    return {
      access: 'full',
      feed_access: 'full',
      profile: null,
      poster: null,
      isOwnProfile: false,
      follow_status: null,
    };
  }
  if ('access' in body && 'profile' in body) {
    return {
      access: body.access,
      feed_access: body.feed_access || 'full',
      profile: body.profile,
      poster: body.poster || null,
      isOwnProfile: !!body.isOwnProfile,
      follow_status: body.follow_status ?? null,
    };
  }
  return {
    access: 'full',
    feed_access: 'full',
    profile: body,
    poster: null,
    isOwnProfile: !!body.isOwnProfile,
    follow_status: null,
  };
}
